/**
 * Chooses the CRM field for one Waterlily field, by searching instead of scrolling.
 *
 * It opens on a short list: what the matcher suggested, with why, then the fields whose type could
 * hold the value (see `lib/field-picker-options`). Typing searches names and labels. A switch at the
 * foot brings back the fields that were left out, for the org that keeps a date in a text field.
 *
 * Each item answers with its own API name through a closure. The search library lowercases the value
 * it filters on, and a CRM's API name is case-sensitive, so the value is never read back from it.
 */
import { useId, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button, Label, Popover, PopoverContent, PopoverTrigger, Switch } from '@design-system';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@client/components/ui/command';
import type { Prospecting } from '@client/api/types';
import type { FieldMapRow } from '@client/hooks/useCrmFieldMap';
import { fieldMapCopy } from '../lib/field-map-copy';
import { UNMAPPED } from '../lib/field-map-groups';
import { pickerOptions } from '../lib/field-picker-options';

interface FieldPickerProps {
  row: FieldMapRow;
  fields: Prospecting.DiscoveredField[];
  crmName: string;
  disabled: boolean;
  onSelect: (sourceField: string) => void;
}

interface FieldItemProps {
  field: Prospecting.DiscoveredField;
  selected: boolean;
  reason?: string | null;
  onSelect: () => void;
}

function FieldItem({ field, selected, reason, onSelect }: FieldItemProps) {
  return (
    <CommandItem
      value={`${field.label} ${field.name}`}
      onSelect={onSelect}
      className="flex items-start gap-2"
    >
      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${selected ? 'text-primary' : 'invisible'}`} />
      <span className="min-w-0">
        <span className="block truncate text-sm text-gray-900">{field.label}</span>
        <span className="block truncate text-xs text-gray-500">
          {field.name}
          {reason ? ` · ${reason}` : ''}
        </span>
      </span>
    </CommandItem>
  );
}

export function FieldPicker({ row, fields, crmName, disabled, onSelect }: FieldPickerProps) {
  const showAllId = useId();
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const isMapped = row.selected !== UNMAPPED;
  const options = pickerOptions({
    fields,
    candidates: row.candidates,
    unsuitableTypes: row.unsuitableTypes,
    selected: isMapped ? row.selected : null,
    showAll,
  });

  const choose = (sourceField: string) => {
    onSelect(sourceField);
    setOpen(false);
  };

  const triggerText = !isMapped
    ? fieldMapCopy.picker.placeholder(crmName)
    : row.selectedField
      ? `${row.selectedField.label} (${row.selectedField.name})`
      : fieldMapCopy.picker.missing(row.selected);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={`${crmName} field for ${row.label}`}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={`truncate ${isMapped ? 'text-gray-900' : 'text-gray-500'}`}>{triggerText}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-gray-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[var(--radix-popover-trigger-width)] min-w-80 p-0"
      >
        <Command>
          <CommandInput placeholder={fieldMapCopy.picker.search} />
          <CommandList>
            <CommandEmpty>{fieldMapCopy.picker.none}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={fieldMapCopy.picker.unmapped}
                onSelect={() => choose(UNMAPPED)}
              >
                <Check className={`h-4 w-4 ${isMapped ? 'invisible' : 'text-primary'}`} />
                {fieldMapCopy.picker.unmapped}
              </CommandItem>
            </CommandGroup>
            {options.suggested.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading={fieldMapCopy.picker.suggested}>
                  {options.suggested.map(({ field, candidate }) => (
                    <FieldItem
                      key={field.name}
                      field={field}
                      selected={row.selected === field.name}
                      reason={fieldMapCopy.matchReason(candidate.matchedOn)}
                      onSelect={() => choose(field.name)}
                    />
                  ))}
                </CommandGroup>
              </>
            )}
            {options.sections.map(section => (
              <CommandGroup
                key={section.group ?? 'own'}
                heading={
                  section.group ? fieldMapCopy.picker.groupFields(section.group) : fieldMapCopy.picker.ownFields
                }
              >
                {section.fields.map(field => (
                  <FieldItem
                    key={field.name}
                    field={field}
                    selected={row.selected === field.name}
                    onSelect={() => choose(field.name)}
                  />
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
        {(options.hiddenCount > 0 || showAll) && (
          <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-3 py-2">
            <div>
              <Label
                htmlFor={showAllId}
                className="font-normal text-gray-900"
              >
                {fieldMapCopy.picker.showAll}
              </Label>
              {!showAll && <p className="text-xs text-gray-500">{fieldMapCopy.picker.showAllHint}</p>}
            </div>
            <Switch
              id={showAllId}
              checked={showAll}
              onCheckedChange={setShowAll}
            />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
